// src/context/AppContext.js
// 앱 전체 상태 관리 — Firebase Auth + Firestore 연동

import React, { createContext, useContext, useReducer, useCallback, useEffect, useRef, useMemo } from 'react';
import {
  firebaseRegister,
  firebaseLogin,
  firebaseLogout,
  onAuthChange,
  fsAddRoom,
  fsUpdateRoom,
  fsDeleteRoom,
  fsSetRooms,
  onRoomsChange,
  fsAddReservation,
  fsUpdateReservation,
  fsBatchUpdateReservations,
  onReservationsChange,
  fsAddCustomer,
  fsUpdateCustomer,
  onCustomersChange,
  fsLogMessage,
  onMessagesChange,
  fsSaveRoomTemplates,
  fsGetRoomTemplates,
  fsGetCommonTemplates,
  fsSaveCommonTemplates,
  onReviewsChange,
  fsMarkReviewRead,
} from '../services/firebase';

// ─── 날짜 유틸 ───
const today = () => new Date().toISOString().split('T')[0];

// ─── 기본 객실 데이터 (온보딩 전 UI용) ───
export const DEFAULT_ROOMS = [
  { id: 'room_1', name: '힐링독채 A', capacity: 4, checkinTime: '15:00', checkoutTime: '11:00', notes: '반려동물 동반 불가. 바비큐장 이용 가능.' },
  { id: 'room_2', name: '힐링독채 B', capacity: 6, checkinTime: '15:00', checkoutTime: '11:00', notes: '소형견 1마리 동반 가능. 퇴실 시 털 정리 부탁드립니다.' },
  { id: 'room_3', name: '단체동 C',   capacity: 15, checkinTime: '14:00', checkoutTime: '11:00', notes: '노래방 기기 구비 (밤 10시까지). 2층 다락방 안전 주의.' },
];

// ─── 초기 상태 ───
function getInitialState() {
  return {
    authLoading: true, // Firebase Auth 초기화 대기
    user: null,
    rooms: [],
    customers: [],
    reservations: [],
    messages: [],
    reviews: [],
    roomTemplates: {},
    commonTemplates: {},
    toast: null,
    prefillCustomer: null,
  };
}

// ─── 리듀서 ───
function reducer(state, action) {
  switch (action.type) {

    // ── 인증 ──
    case 'SET_AUTH_LOADING':
      return { ...state, authLoading: action.payload };
    case 'SET_USER':
      return { ...state, user: action.payload, authLoading: false };
    case 'LOGOUT':
      return { ...getInitialState(), authLoading: false };

    // ── Firestore 실시간 데이터 ──
    case 'SET_ROOMS':
      return { ...state, rooms: action.payload };
    case 'DELETE_ROOM':
      return { ...state, rooms: state.rooms.filter(r => r.id !== action.payload) };
    case 'SET_RESERVATIONS':
      return { ...state, reservations: action.payload };
    case 'SET_CUSTOMERS':
      return { ...state, customers: action.payload };
    case 'SET_MESSAGES':
      return { ...state, messages: action.payload };
    case 'SET_REVIEWS':
      return { ...state, reviews: action.payload };
    case 'SET_ROOM_TEMPLATES':
      return { ...state, roomTemplates: action.payload };
    case 'SET_COMMON_TEMPLATES':
      return { ...state, commonTemplates: action.payload };

    // ── UI ──
    case 'SET_TOAST':
      return { ...state, toast: action.payload };
    case 'CLEAR_TOAST':
      return { ...state, toast: null };
    case 'SET_PREFILL_CUSTOMER':
      return { ...state, prefillCustomer: action.payload };
    case 'CLEAR_PREFILL':
      return { ...state, prefillCustomer: null };

    default:
      return state;
  }
}

// ─── Context ───
const AppContext = createContext(null);

export function AppProvider({ children }) {
  const [state, dispatch] = useReducer(reducer, getInitialState());
  // Firestore 실시간 리스너 해제용 refs
  const unsubRooms = useRef(null);
  const unsubReservations = useRef(null);
  const unsubCustomers = useRef(null);
  const unsubMessages = useRef(null);
  const unsubReviews = useRef(null);
  const toastTimerRef = useRef(null);

  // ── Firebase Auth 상태 감지 ──
  useEffect(() => {
    const cleanupListeners = () => {
      unsubRooms.current?.();
      unsubReservations.current?.();
      unsubCustomers.current?.();
      unsubMessages.current?.();
      unsubRooms.current = null;
      unsubReservations.current = null;
      unsubCustomers.current = null;
      unsubMessages.current = null;
      unsubReviews.current?.();
      unsubReviews.current = null;
    };

    const unsub = onAuthChange(async (user) => {
      // 이전 유저 리스너 항상 먼저 정리 (재로그인·유저 전환 시 중복 구독 방지)
      cleanupListeners();

      if (!user) {
        dispatch({ type: 'LOGOUT' });
        return;
      }

      dispatch({ type: 'SET_USER', payload: user });

      // 객실 템플릿 + 공통 템플릿 1회 로드
      try {
        const [roomTmpl, commonTmpl] = await Promise.all([
          fsGetRoomTemplates(user.uid),
          fsGetCommonTemplates(user.uid),
        ]);
        dispatch({ type: 'SET_ROOM_TEMPLATES', payload: roomTmpl });
        dispatch({ type: 'SET_COMMON_TEMPLATES', payload: commonTmpl });
      } catch (e) {
        console.error('템플릿 로드 실패:', e);
      }

      // 실시간 리스너 등록
      unsubRooms.current = onRoomsChange(user.uid, (rooms) => {
        dispatch({ type: 'SET_ROOMS', payload: rooms });
      });
      unsubReservations.current = onReservationsChange(user.uid, (reservations) => {
        dispatch({ type: 'SET_RESERVATIONS', payload: reservations });
      });
      unsubCustomers.current = onCustomersChange(user.uid, (customers) => {
        dispatch({ type: 'SET_CUSTOMERS', payload: customers });
      });
      unsubMessages.current = onMessagesChange(user.uid, (messages) => {
        dispatch({ type: 'SET_MESSAGES', payload: messages });
      });
      if (!user.isAnonymous) {
        unsubReviews.current = onReviewsChange(user.uid, (reviews) => {
          dispatch({ type: 'SET_REVIEWS', payload: reviews });
        });
      }
    });

    return () => {
      unsub();
      cleanupListeners();
    };
  }, []);

  // ── 인증 ──
  const login = useCallback(async (email, password) => {
    const user = await firebaseLogin(email, password);
    dispatch({ type: 'SET_USER', payload: user });
    return user;
  }, []);

  const register = useCallback(async ({ email, password, pensionName, phone }) => {
    const user = await firebaseRegister({ email, password, pensionName, phone });
    dispatch({ type: 'SET_USER', payload: user });
    return user;
  }, []);

  const logout = useCallback(async () => {
    await firebaseLogout();
    dispatch({ type: 'LOGOUT' });
  }, []);

  // ── 객실 ──
  const addRoom = useCallback(async (data) => {
    const uid = state.user?.uid;
    if (!uid) return;
    const id = `room_${Date.now()}`;
    await fsAddRoom(uid, { ...data, id });
    return id;
  }, [state.user]);

  const updateRoom = useCallback(async (data) => {
    const uid = state.user?.uid;
    if (!uid) return;
    await fsUpdateRoom(uid, data.id, data);
  }, [state.user]);

  const deleteRoom = useCallback(async (id) => {
    const uid = state.user?.uid;
    if (!uid) throw new Error('로그인이 필요합니다');
    // 1. Firestore 객실 삭제 (핵심 - 실패 시 throw)
    await fsDeleteRoom(uid, id);
    // 2. 즉시 로컬 state 반영 (onSnapshot 지연 대비)
    dispatch({ type: 'DELETE_ROOM', payload: id });
    // 3. 템플릿 정리 (부수 처리 - 실패해도 삭제는 성공)
    try {
      const updated = { ...state.roomTemplates };
      delete updated[id];
      dispatch({ type: 'SET_ROOM_TEMPLATES', payload: updated });
      await fsSaveRoomTemplates(uid, updated);
    } catch (e) {
      console.warn('[deleteRoom] 템플릿 정리 실패 (객실 삭제는 완료됨):', e);
    }
  }, [state.user, state.roomTemplates]);

  // 온보딩용: 여러 객실 한 번에 저장
  const setRooms = useCallback(async (rooms) => {
    const uid = state.user?.uid;
    if (!uid) return;
    await fsSetRooms(uid, rooms);
  }, [state.user]);

  // ── 객실 템플릿 ──
  const setRoomTemplate = useCallback(async (roomId, type, template) => {
    const uid = state.user?.uid;
    if (!uid) return;
    const updated = {
      ...state.roomTemplates,
      [roomId]: { ...(state.roomTemplates[roomId] || {}), [type]: template },
    };
    dispatch({ type: 'SET_ROOM_TEMPLATES', payload: updated });
    await fsSaveRoomTemplates(uid, updated);
  }, [state.user, state.roomTemplates]);

  const resetRoomTemplate = useCallback(async (roomId, type) => {
    const uid = state.user?.uid;
    if (!uid) return;
    const prev = { ...(state.roomTemplates[roomId] || {}) };
    delete prev[type];
    const updated = { ...state.roomTemplates, [roomId]: prev };
    if (Object.keys(prev).length === 0) delete updated[roomId];
    dispatch({ type: 'SET_ROOM_TEMPLATES', payload: updated });
    await fsSaveRoomTemplates(uid, updated);
  }, [state.user, state.roomTemplates]);

  // ── 공통 템플릿 ──
  const setCommonTemplate = useCallback(async (type, template) => {
    const uid = state.user?.uid;
    if (!uid) return;
    const updated = { ...state.commonTemplates, [type]: template };
    dispatch({ type: 'SET_COMMON_TEMPLATES', payload: updated });
    await fsSaveCommonTemplates(uid, updated);
  }, [state.user, state.commonTemplates]);

  const resetCommonTemplate = useCallback(async (type) => {
    const uid = state.user?.uid;
    if (!uid) return;
    const updated = { ...state.commonTemplates };
    delete updated[type];
    dispatch({ type: 'SET_COMMON_TEMPLATES', payload: updated });
    await fsSaveCommonTemplates(uid, updated);
  }, [state.user, state.commonTemplates]);

  // ── 예약 ──
  const addReservation = useCallback(async (data) => {
    const uid = state.user?.uid;
    if (!uid) return;
    await fsAddReservation(uid, { ...data, checkinSent: false, checkoutSent: false });
  }, [state.user]);

  const updateReservation = useCallback(async (data) => {
    const uid = state.user?.uid;
    if (!uid) return;
    await fsUpdateReservation(uid, data.id, data);
  }, [state.user]);

  const cancelReservation = useCallback(async (id) => {
    const uid = state.user?.uid;
    if (!uid) return;
    await fsUpdateReservation(uid, id, { status: 'cancelled' });
  }, [state.user]);

  const markCheckinSent = useCallback(async (id) => {
    const uid = state.user?.uid;
    if (!uid) return;
    await fsUpdateReservation(uid, id, { checkinSent: true });
  }, [state.user]);

  const markCheckoutSent = useCallback(async (ids) => {
    const uid = state.user?.uid;
    if (!uid) return;
    await fsBatchUpdateReservations(uid, ids, { checkoutSent: true });
  }, [state.user]);

  // ── 고객 ──
  const addCustomer = useCallback(async (data) => {
    const uid = state.user?.uid;
    if (!uid) return;
    // 전화번호 중복 체크 (로컬 state 기준)
    const existing = state.customers.find(c => c.phone === data.phone);
    if (existing) {
      const visits = (existing.visits || 1) + 1;
      await fsUpdateCustomer(uid, existing.id, {
        visits,
        lastVisit: data.lastVisit || today(),
        roomId: data.roomId || existing.roomId,
        roomName: data.roomName || existing.roomName,
        guests: data.guests || existing.guests,
        badges: visits >= 3
          ? [...new Set([...(existing.badges || []), '단골'])]
          : existing.badges || [],
      });
      return;
    }
    await fsAddCustomer(uid, {
      name: data.name,
      phone: data.phone,
      roomId: data.roomId || null,
      roomName: data.roomName || '',
      guests: data.guests || 0,
      visits: 1,
      channel: data.channel || '',
      firstVisit: data.firstVisit || today(),
      lastVisit: data.lastVisit || today(),
      lastDiscount: null,
      marketing_consent: false,
      marketing_consent_at: null,
      opt_out: false,
      badges: parseInt(data.guests) >= 8 ? ['단체'] : [],
    });
  }, [state.user, state.customers]);

  const updateCustomer = useCallback(async (data) => {
    const uid = state.user?.uid;
    if (!uid) return;
    await fsUpdateCustomer(uid, data.id, data);
  }, [state.user]);

  const markReviewRead = useCallback(async (reviewId) => {
    const uid = state.user?.uid;
    if (!uid) return;
    await fsMarkReviewRead(uid, reviewId);
  }, [state.user]);

  // 기존 고객 일괄 업로드 — [{name, phone}] 배열을 받아 중복 체크 후 저장
  const importCustomers = useCallback(async (entries) => {
    const uid = state.user?.uid;
    if (!uid) return [];
    const imported = [];
    for (const entry of entries) {
      const existing = state.customers.find(c => c.phone === entry.phone);
      if (existing) {
        if (!existing.badges?.includes('기존')) {
          await fsUpdateCustomer(uid, existing.id, {
            badges: [...new Set([...(existing.badges || []), '기존'])],
          });
        }
        imported.push({ ...existing, name: entry.name, phone: entry.phone });
      } else {
        await fsAddCustomer(uid, {
          name: entry.name,
          phone: entry.phone,
          roomId: null, roomName: '', guests: 0, visits: 0, channel: '',
          firstVisit: null, lastVisit: null, lastDiscount: null,
          marketing_consent: false, marketing_consent_at: null, opt_out: false,
          badges: ['기존'],
        });
        imported.push({ name: entry.name, phone: entry.phone, badges: ['기존'] });
      }
    }
    return imported;
  }, [state.user, state.customers]);

  const toggleMarketingConsent = useCallback(async (id) => {
    const uid = state.user?.uid;
    if (!uid) return;
    const customer = state.customers.find(c => c.id === id);
    if (!customer) return;
    await fsUpdateCustomer(uid, id, {
      marketing_consent: !customer.marketing_consent,
      marketing_consent_at: !customer.marketing_consent ? new Date().toISOString() : customer.marketing_consent_at,
    });
  }, [state.user, state.customers]);

  const updateLastDiscount = useCallback(async (customerId) => {
    const uid = state.user?.uid;
    if (!uid) return;
    await fsUpdateCustomer(uid, customerId, { lastDiscount: new Date().toISOString() });
  }, [state.user]);

  // ── 메시지 로그 ──
  const logMessage = useCallback(async (data) => {
    const uid = state.user?.uid;
    if (!uid) return;
    await fsLogMessage(uid, {
      customerId: data.customerId || null,
      customerName: data.customerName,
      phone: data.phone,
      templateId: data.templateId,
      templateName: data.templateName,
      roomId: data.roomId || null,
      messageText: data.messageText || '',
      status: 'sent',
    });
  }, [state.user]);

  // ── UI ──
  const showToast = useCallback((message, type = 'success', duration = 2800) => {
    if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
    dispatch({ type: 'SET_TOAST', payload: { message, type } });
    toastTimerRef.current = setTimeout(() => {
      dispatch({ type: 'CLEAR_TOAST' });
      toastTimerRef.current = null;
    }, duration);
  }, []);

  const sendToCustomer = useCallback((customer) => {
    dispatch({ type: 'SET_PREFILL_CUSTOMER', payload: customer });
  }, []);

  const clearPrefill = useCallback(() => {
    dispatch({ type: 'CLEAR_PREFILL' });
  }, []);

  // ── 계산 함수 ──
  const getDaysSinceVisit = useCallback((lastVisit) => {
    if (!lastVisit) return 0;
    const diff = Date.now() - new Date(lastVisit).getTime();
    return Math.floor(diff / 86400000);
  }, []);

  // useMemo로 캐싱: customers/reservations 변경 시에만 재계산 (O(n×m))
  const enhancedCustomers = useMemo(() => {
    const todayDate = today();
    return state.customers.map(c => {
      const completed = state.reservations.filter(r =>
        r.phone === c.phone &&
        r.status !== 'cancelled' &&
        r.checkout && r.checkout <= todayDate
      );
      const visits = completed.length;
      // slice()로 원본 배열 보호 후 정렬
      const lastVisit = visits > 0
        ? completed.slice().sort((a, b) => b.checkout.localeCompare(a.checkout))[0].checkout
        : null;
      const isRegular = visits >= 3;
      const isGroup = (c.guests || 0) >= 8 || c.badges?.includes('단체');
      const badges = [...new Set([
        ...(isRegular ? ['단골'] : []),
        ...(isGroup ? ['단체'] : []),
      ])];
      return { ...c, visits, lastVisit: lastVisit ?? c.firstVisit ?? null, badges };
    });
  }, [state.customers, state.reservations]);

  // 하위 호환성 유지를 위해 함수 형태도 제공
  const getEnhancedCustomers = useCallback(() => enhancedCustomers, [enhancedCustomers]);

  // 고객별 현재 상태 계산 (방문예정 / 투숙중 / 방문완료) — useMemo로 캐싱
  const customersWithStatus = useMemo(() => {
    const todayDate = today();
    return enhancedCustomers.map(c => {
      const active = state.reservations.filter(r =>
        r.phone === c.phone && r.status !== 'cancelled' && r.checkin && r.checkout
      );

      // 투숙 중: checkin <= today < checkout
      const staying = active.find(r => r.checkin <= todayDate && r.checkout > todayDate);
      if (staying) {
        return { ...c, status: 'staying', statusCheckin: staying.checkin, statusCheckout: staying.checkout };
      }

      // 방문 예정: checkin > today (가장 가까운 것)
      const upcoming = active
        .filter(r => r.checkin > todayDate)
        .sort((a, b) => a.checkin.localeCompare(b.checkin))[0];
      if (upcoming) {
        const daysUntil = Math.ceil(
          (new Date(upcoming.checkin + 'T00:00:00').getTime() - new Date(todayDate + 'T00:00:00').getTime()) / 86400000
        );
        return { ...c, status: 'upcoming', statusCheckin: upcoming.checkin, statusCheckout: upcoming.checkout, daysUntil };
      }

      // 방문 완료 또는 이력 없음
      return { ...c, status: c.visits > 0 ? 'completed' : 'none' };
    });
  }, [enhancedCustomers, state.reservations]);

  const getCustomersWithStatus = useCallback(() => customersWithStatus, [customersWithStatus]);

  // 재방문 추천 대상 — useMemo로 캐싱
  const revisitCandidates = useMemo(() => {
    const sixtyDaysAgo = new Date(Date.now() - 60 * 86400000);
    const thirtyDaysAgo = new Date(Date.now() - 30 * 86400000);
    return enhancedCustomers.filter(c => {
      if (c.visits === 0 || !c.lastVisit) return false;
      if (new Date(c.lastVisit) > sixtyDaysAgo) return false;
      if (c.lastDiscount && new Date(c.lastDiscount) > thirtyDaysAgo) return false;
      return true;
    });
  }, [enhancedCustomers]);

  const getRevisitCandidates = useCallback(() => revisitCandidates, [revisitCandidates]);

  const todayCheckins = useMemo(() => {
    const t = today();
    return state.reservations.filter(r => r.checkin === t && r.status !== 'cancelled');
  }, [state.reservations]);

  const todayCheckouts = useMemo(() => {
    const t = today();
    return state.reservations.filter(r => r.checkout === t && r.status !== 'cancelled');
  }, [state.reservations]);

  const getTodayCheckins = useCallback(() => todayCheckins, [todayCheckins]);
  const getTodayCheckouts = useCallback(() => todayCheckouts, [todayCheckouts]);

  const value = useMemo(() => ({
    state,
    dispatch,
    // 인증
    login,
    register,
    logout,
    // 객실
    addRoom,
    updateRoom,
    deleteRoom,
    setRooms,
    setRoomTemplate,
    resetRoomTemplate,
    setCommonTemplate,
    resetCommonTemplate,
    // 예약
    addReservation,
    updateReservation,
    cancelReservation,
    markCheckinSent,
    markCheckoutSent,
    // 고객
    addCustomer,
    updateCustomer,
    importCustomers,
    toggleMarketingConsent,
    // 리뷰
    markReviewRead,
    updateLastDiscount,
    // 메시지
    logMessage,
    // UI
    showToast,
    sendToCustomer,
    clearPrefill,
    // 계산
    getDaysSinceVisit,
    getEnhancedCustomers,
    getCustomersWithStatus,
    getRevisitCandidates,
    getTodayCheckins,
    getTodayCheckouts,
  }), [
    state,
    dispatch,
    login, register, logout,
    addRoom, updateRoom, deleteRoom, setRooms, setRoomTemplate, resetRoomTemplate,
    setCommonTemplate, resetCommonTemplate,
    addReservation, updateReservation, cancelReservation, markCheckinSent, markCheckoutSent,
    addCustomer, updateCustomer, importCustomers, toggleMarketingConsent,
    markReviewRead, updateLastDiscount,
    logMessage,
    showToast, sendToCustomer, clearPrefill,
    getDaysSinceVisit, getEnhancedCustomers, getCustomersWithStatus,
    getRevisitCandidates, getTodayCheckins, getTodayCheckouts,
  ]);

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
}

export function useApp() {
  const ctx = useContext(AppContext);
  if (!ctx) throw new Error('useApp must be used within AppProvider');
  return ctx;
}
